import { Search } from 'lucide-react';
import type { SocialComment, CommentPlatform } from '@/types/outreach';
import styles from './CommentFeed.module.css';

function formatRelativeTime(ts: string): string {
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const PLATFORM_LABELS: Record<CommentPlatform, string> = {
  FACEBOOK: 'FB',
  INSTAGRAM: 'IG',
  TIKTOK: 'TT',
};

function PlatformBadge({ platform }: { platform: CommentPlatform }) {
  return (
    <span className={`${styles.platformBadge} ${styles[`platform${platform}`]}`}>
      {PLATFORM_LABELS[platform]}
    </span>
  );
}

function SentimentDot({ score }: { score: number | null }) {
  if (score === null) return null;
  let cls = styles.sentimentNeutral;
  if (score >= 0.5) cls = styles.sentimentPositive;
  else if (score <= -0.5) cls = styles.sentimentNegative;
  return (
    <span className={`${styles.sentimentDot} ${cls}`} title={`Sentiment: ${score.toFixed(2)}`} />
  );
}

interface CommentRowProps {
  comment: SocialComment;
}

function CommentRow({ comment }: CommentRowProps) {
  return (
    <div className={`${styles.row} ${comment.isReply ? styles.rowIndented : ''}`}>
      <div className={styles.rowLeft}>
        <PlatformBadge platform={comment.platform} />
        {comment.authorProfilePic ? (
          <img src={comment.authorProfilePic} alt={comment.authorName} className={styles.avatar} />
        ) : (
          <div className={styles.avatarPlaceholder}>
            {comment.authorName.charAt(0).toUpperCase()}
          </div>
        )}
        <div className={styles.content}>
          <div className={styles.meta}>
            <span className={styles.author}>{comment.authorName}</span>
            <span className={styles.time}>{formatRelativeTime(comment.timestamp)}</span>
            <SentimentDot score={comment.sentimentScore} />
          </div>
          <p className={styles.message}>{comment.message}</p>
          {comment.autoReplied && comment.autoReplyContent && (
            <div className={styles.replyPreview}>
              <span className={styles.replyLabel}>Auto-reply:</span>
              <span className={styles.replyText}>{comment.autoReplyContent}</span>
            </div>
          )}
        </div>
      </div>
      <div className={styles.rowRight}>
        {comment.autoReplied && <span className={styles.autoRepliedBadge}>Replied</span>}
      </div>
    </div>
  );
}

interface CommentFeedProps {
  comments: SocialComment[];
  isLoading: boolean;
  search: string;
  onSearchChange: (v: string) => void;
  total?: number;
}

export function CommentFeed({
  comments,
  isLoading,
  search,
  onSearchChange,
  total,
}: CommentFeedProps) {
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.searchWrapper}>
          <Search className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search comments..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        {total !== undefined && <span className={styles.countLabel}>{total} comments</span>}
      </div>

      <div className={styles.list}>
        {isLoading && <div className={styles.empty}>Loading comments...</div>}
        {!isLoading && comments.length === 0 && (
          <div className={styles.empty}>No comments found for this filter.</div>
        )}
        {comments.map((c) => (
          <CommentRow key={c.id} comment={c} />
        ))}
      </div>
    </div>
  );
}
